import secureApi from '../../../utils/secureApi';
import { SHOP_ENDPOINTS } from '../endpoints';
import {
  SellerLocation,
  SellerLocationListResponse,
  AddSellerLocationRequest,
  UpdateSellerLocationRequest,
} from '../types/sellerTypes';

/**
 * Get list of seller locations with pagination and filters
 */
export const getSellerLocations = async (params: {
  pageNum?: number;
  seller?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'All';
}): Promise<SellerLocationListResponse> => {
  try {
    const queryParams: any = {};
    if (params.pageNum) queryParams.pageNum = params.pageNum;
    if (params.seller) queryParams.seller = params.seller;
    if (params.status && params.status !== 'All') {
      queryParams.status = params.status;
    }

    const response = await secureApi.get<any>(SHOP_ENDPOINTS.SELLER_LOCATIONS.LIST, {
      params: queryParams,
    });

    // Handle API response structure: { data: { paginatedResults: [], totalCount: number }, status, message }
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return {
      paginatedResults: (apiData.paginatedResults || apiData.locations || []).map((location: any) => ({
        ...location,
        _id: location._id,
        // Ensure fulfillmentCapabilities is an array
        fulfillmentCapabilities: location.fulfillmentCapabilities || [],
      })),
      totalCount: apiData.totalCount || 0,
      totalPages: apiData.totalPages || Math.ceil((apiData.totalCount || 0) / 10),
      page: params.pageNum || 1,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get seller location by ID
 */
export const getSellerLocationById = async (id: string): Promise<SellerLocation> => {
  try {
    const response = await secureApi.get<any>(SHOP_ENDPOINTS.SELLER_LOCATIONS.GET_BY_ID(id));
    
    // Handle API response structure: { data: {...}, status, message, statusCode }
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return {
      ...apiData,
      _id: apiData._id,
      fulfillmentCapabilities: apiData.fulfillmentCapabilities || [],
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Add new seller location
 */
export const addSellerLocation = async (payload: AddSellerLocationRequest): Promise<SellerLocation> => {
  try {
    const response = await secureApi.post<any>(SHOP_ENDPOINTS.SELLER_LOCATIONS.ADD, payload);
    
    // Handle API response structure
    const apiData = (response as any)?.data || response;
    
    return {
      ...apiData,
      _id: apiData._id,
    };
  } catch (error: any) {
    // Re-throw the error so the component can handle it
    throw error;
  }
};

/**
 * Update seller location
 */
export const updateSellerLocation = async (
  id: string,
  payload: UpdateSellerLocationRequest
): Promise<SellerLocation> => {
  try {
    const response = await secureApi.post<any>(SHOP_ENDPOINTS.SELLER_LOCATIONS.UPDATE(id), payload);
    
    // Handle API response structure
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return {
      ...apiData,
      _id: apiData._id,
    };
  } catch (error: any) {
    throw error;
  }
};

/**
 * Delete seller location
 */
export const deleteSellerLocation = async (id: string): Promise<void> => {
  try {
    await secureApi.post<any>(SHOP_ENDPOINTS.SELLER_LOCATIONS.DELETE(id));
  } catch (error: any) {
    throw error;
  }
};

/**
 * Get all available locations for selection in forms
 */
export const getAllLocations = async (): Promise<any[]> => {
  try {
    const response = await secureApi.get<any>(SHOP_ENDPOINTS.SELLER_LOCATIONS.ALL);
    
    // Handle API response structure: { data: [...], status, message, statusCode }
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return apiData || [];
  } catch (error: any) {
    throw error;
  }
};

/**
 * Check if a seller has any locations
 */
export const checkSellerHasLocations = async (sellerId: string): Promise<boolean> => {
  try {
    const response = await getSellerLocations({
      pageNum: 1,
      seller: sellerId,
      status: 'All'
    });
    return response.totalCount > 0;
  } catch (error) {
    return false;
  }
};

/**
 * Update seller location status (activate/deactivate)
 */
export const updateSellerLocationStatus = async (
  id: string,
  status: 'ACTIVE' | 'INACTIVE'
): Promise<SellerLocation> => {
  try {
    // Get the current location data first
    const currentLocation = await getSellerLocationById(id);
    
    // Create minimal update payload with just the status change
    const payload: any = {
      seller: currentLocation.seller,
      locationCode: currentLocation.locationCode,
      type: currentLocation.type,
      fulfillmentCapabilities: currentLocation.fulfillmentCapabilities,
      revenueCenterCode: currentLocation.revenueCenterCode || null,
      status: status,
    };

    // Add additional fields based on location type
    if (currentLocation.type === 'WAREHOUSE' && currentLocation.name) {
      payload.name = currentLocation.name;
    }

    // Address is always required by the API
    if (currentLocation.address) {
      payload.address = currentLocation.address;
    } else {
      payload.address = {
        line1: '',
        city: '',
        state: '',
        pincode: '',
      };
    }

    // Add academy and coachingCenter for ACADEMY type
    if (currentLocation.type === 'ACADEMY' && currentLocation.academy && currentLocation.coachingCenter) {
      payload.academy = currentLocation.academy;
      payload.coachingCenter = currentLocation.coachingCenter;
    }

    const response = await secureApi.post<any>(SHOP_ENDPOINTS.SELLER_LOCATIONS.UPDATE(id), payload);
    
    // Handle API response structure
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return {
      ...apiData,
      _id: apiData._id,
    };
  } catch (error: any) {
    throw error;
  }
};
